import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { createWorld, addEntity, addComponent, defineQuery } from 'bitecs';
import { MenuSystem } from '../systems/menu.js';
import { UIManager } from '../systems/ui/UIManager.js';
import { LoadingScreen } from '../systems/loading.js';
import { Assets } from 'pixi.js';
import { RenderSystem, initRenderSystem, getEntityTypeInfo } from '../systems/render.js';
import { updateConfig, TILE_SIZE, TILE_SCALE, PLAYER_SIZE, RENDER_RADIUS, SUB_TILE_SIZE, ENTITY_BASE_RADIUS, PLAYER_SPEED, PLAYER_ACCELERATION, PLAYER_DECELERATION, PLAYER_MAX_SPEED, MAP_WIDTH, MAP_HEIGHT, TIME_SCALE, CURRENT_MODE, PLAYER_STATS } from '../config.js';
import { updateEntityCount } from '../entityConfigUtils.js';
import { mapGenerationSystem } from '../systems/map.js';
import { plantGenerationSystem, plantGrowthSystem } from '../systems/plants.js';
import { rockGenerationSystem } from '../systems/rocks.js';
import { animalGenerationSystem, animalBehaviorSystem } from '../systems/animals.js';
import { createTimeSystem, timeSystem } from '../systems/time.js';
import { movementSystem } from '../systems/movement.js';
import { wetnessSystem } from '../systems/wetness.js';
import { createUISystem } from '../systems/ui.js';
import { LightingSystem } from '../systems/lighting.js';
import { tileManager, TILE_TYPES } from '../systems/tileManager.js';
import { AssetManager, ASSET_BUNDLES } from '../assets/assetManager.js';
import { hungerSystem, thirstSystem } from '../systems/hunger.js';
import { healthSystem } from '../systems/health.js';
import { aiSystem } from '../systems/ai.js';
import { ecosystemSystem } from '../systems/ecosystem.js';
import { performanceOptimizer, chunkCullingSystem } from '../systems/performanceOptimizer.js';
import { balancedSpawner } from '../systems/balancedSpawner.js';
import { SimulationSystem } from '../systems/simulation.js';
import { 
    Position, 
    Velocity, 
    Movement, 
    Input, 
    Sprite as SpriteComponent,
    Health, 
    Player, 
    Inventory, 
    Wetness, 
    Hunger, 
    Thirst, 
    SoundLevel,
    Structure,
    Production,
    Animal,
    AI,
    AI_STATE,
    ANIMAL_TYPES,
    initializeWorld
} from '../world.js';
import { createBee, setApp } from '../entities/bee.js';
import { wanderSystem } from '../systems/wander.js';
import { createHive, setApp as setHiveApp } from '../entities/hive.js';
import { beeSystem, setApp as setBeeSystemApp } from '../systems/beeSystem.js';
import { structureGenerationSystem } from '../systems/structures.js';
import { createBeeEater } from '../entities/bee_eater.js';
import { createRabbit, setApp as setRabbitApp } from '../entities/rabbit.js';

// Define queries
const playerQuery = defineQuery([Player]);

export class GameEngine {
    constructor() {
        this.app = new Application();
        this.world = null;
        this.worldContainer = null;
        this.gameplayContainer = null;
        this.menuSystem = null;
        this.uiManager = null;
        this.loadingScreen = null;
        this.resources = null;
        this.isRunning = false;
        this.systems = new Map();
        this.gameComponents = new Map();
        this.uiContainer = null;
        this.uiSystem = null;
        this.renderSystem = null;
        this.lightingSystem = null;
        this.assetManager = null;
    }

    registerGameComponent(name, component) {
        this.gameComponents.set(name, component);
    }

    showGameComponents() {
        for (const [name, component] of this.gameComponents) {
            if (component.show) component.show();
            if (component.visible !== undefined) component.visible = true;
        }
    }

    hideGameComponents() {
        for (const [name, component] of this.gameComponents) {
            if (component.hide) component.hide();
            if (component.visible !== undefined) component.visible = false;
        }
    }

    async initialize(assetManager) {
        console.log('[ENGINE] Initializing engine');
        // Initialize PIXI application
        await this.app.init({
            width: window.innerWidth,
            height: window.innerHeight,
            backgroundColor: 0x000000,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
            antialias: true,
            powerPreference: 'high-performance',
        });

        // Use provided asset manager
        this.assetManager = assetManager;
        this.app.assetManager = this.assetManager; // Share with app

        // Load initial assets
        await this.loadAssets();

        // Set up the canvas
        document.body.appendChild(this.app.canvas);
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.overflow = 'hidden';
        this.app.canvas.style.position = 'fixed';
        this.app.canvas.style.width = '100vw';
        this.app.canvas.style.height = '100vh';
        this.app.canvas.style.left = '0';
        this.app.canvas.style.top = '0';

        // Create menu system (now assetManager is available)
        this.menuSystem = new MenuSystem(this.app);
        this.app.menuSystem = this.menuSystem;
        this.menuSystem.show();

        // Create UI manager
        this.uiManager = new UIManager(this.app);

        // Create gameplay container
        this.gameplayContainer = new Container();
        this.gameplayContainer.sortableChildren = true;
        this.gameplayContainer.visible = false;
        this.app.stage.addChild(this.gameplayContainer);

        // Create and initialize world container
        this.initializeWorldContainer();

        // Set up event listeners
        this.setupEventListeners();

        console.log('[ENGINE] Engine initialized');
        return this;
    }

    initializeWorldContainer() {
        console.log('[ENGINE] Initializing world container');
        // Remove existing world container if it exists
        if (this.worldContainer) {
            this.gameplayContainer.removeChild(this.worldContainer);
            this.worldContainer.destroy();
        }

        // Create new world container
        this.worldContainer = new Container();
        this.worldContainer.name = 'worldContainer';
        this.worldContainer.sortableChildren = true;
        this.worldContainer.x = window.innerWidth / 2;
        this.worldContainer.y = window.innerHeight / 2;
        this.worldContainer.scale.set(1);
        this.worldContainer.visible = true;
        this.gameplayContainer.addChild(this.worldContainer);
        
        // Share world container with app for other systems to access
        this.app.worldContainer = this.worldContainer;
    }

    static async start(assetManager) {
        const engine = new GameEngine();
        try {
            await engine.initialize(assetManager);
        } catch (e) {
            console.error('Failed to initialize game:', e);
        }
        return engine;
    }

    setupEventListeners() {
        // Handle config updates
        this.app.stage.on('configUpdate', ({ key, value }) => {
            console.log('Config update:', key, value);
            updateConfig(key, value);
        });

        // Handle window resize
        const handleResize = () => {
            this.app.renderer.resize(window.innerWidth, window.innerHeight);
            if (this.worldContainer) {
                this.worldContainer.x = window.innerWidth / 2;
                this.worldContainer.y = window.innerHeight / 2;
            }
        };

        // Add resize listener
        window.addEventListener('resize', handleResize);

        // Add keyboard handlers for movement and view toggles
        window.addEventListener('keydown', (e) => {
            if (!this.world) return;
            
            const players = playerQuery(this.world);
            if (players.length === 0) return;
            const player = players[0];

            switch(e.key.toLowerCase()) {
                case 'w':
                    Input.up[player] = 1;
                    break;
                case 's':
                    Input.down[player] = 1;
                    break;
                case 'a':
                    Input.left[player] = 1;
                    break;
                case 'd':
                    Input.right[player] = 1;
                    break;
                case 'shift':
                    SoundLevel.isRunning[player] = 1;
                    break;
                case 'b':
                    if (!this.world.isPaused) {
                        console.log('[VIEW] Toggling biome view');
                        this.world.showBiome = !this.world.showBiome;
                        this.world.showHeightmap = false;
                    }
                    break;
                case 'm':
                    if (!this.world.isPaused) {
                        console.log('[VIEW] Toggling minimap');
                        tileManager.toggleMinimap();
                    }
                    break;
                case 'l':
                    if (!this.world.isPaused) {
                        console.log('[VIEW] Toggling tile borders');
                        tileManager.toggleTileBorders();
                    }
                    break;
            }
        });

        window.addEventListener('keyup', (e) => {
            if (!this.world || this.world.isPaused) return;
            
            const players = playerQuery(this.world);
            if (players.length === 0) return;
            const player = players[0];

            switch(e.key.toLowerCase()) {
                case 'w':
                    Input.up[player] = 0;
                    break;
                case 's':
                    Input.down[player] = 0;
                    break;
                case 'a':
                    Input.left[player] = 0;
                    break;
                case 'd':
                    Input.right[player] = 0;
                    break;
                case 'shift':
                    SoundLevel.isRunning[player] = 0;
                    break;
            }
        });
    }

    async startGame() {
        try {
            // Create loading screen
            this.loadingScreen = new LoadingScreen(this.app);
            this.loadingScreen.show();
            this.loadingScreen.updateProgress(0, 'Initializing game...');

            // Hide menu if it exists
            if (this.menuSystem) {
                this.menuSystem.hide();
            }

            // Clean up the existing render system instance if it exists
            if (this.renderSystem && typeof this.renderSystem.cleanup === 'function') {
                console.log('[CLEANUP] Cleaning up existing RenderSystem instance');
                this.renderSystem.cleanup();
                this.renderSystem = null; // Clear the reference
            } else {
                 console.log('[CLEANUP] No existing RenderSystem instance found or cleanup method missing.');
                 // If the old cleanupRenderSystem was global or static, ensure it's not called.
            }

            // Load all assets (both images and audio)
            this.loadingScreen.updateProgress(20, 'Loading assets...');
            await this.assetManager.loadAllAssets();
            this.loadingScreen.updateProgress(40, 'Assets loaded');

            // Reinitialize world container
            this.loadingScreen.updateProgress(60, 'Creating world...');
            this.initializeWorldContainer();

            // Initialize game world
            this.loadingScreen.updateProgress(80, 'Initializing game world...');
            await this.initializeGameWorld();

            // Initialize systems
            this.loadingScreen.updateProgress(90, 'Initializing systems...');
            await this.initializeSystems();

            // Register game components
            this.registerGameComponent('worldContainer', this.worldContainer);
            this.registerGameComponent('gameplayContainer', this.gameplayContainer);

            // Show gameplay container
            console.log('[ENGINE] Showing gameplay container');
            this.gameplayContainer.visible = true;

            // Start game loop
            console.log('[ENGINE] Starting game loop');
            this.startGameLoop();

            // Hide loading screen
            this.loadingScreen.updateProgress(100, 'Ready!');
            setTimeout(() => {
                this.loadingScreen.hide();
                this.loadingScreen.destroy();
                this.loadingScreen = null;
            }, 500); // Give a small delay to show 100%

            console.log('[ENGINE] Game started successfully');
        } catch (error) {
            console.error('[ENGINE] Error starting game:', error);
            if (this.loadingScreen) {
                this.loadingScreen.hide();
                this.loadingScreen.destroy();
                this.loadingScreen = null;
            }
            throw error;
        }
    }

    async loadAssets() {
        console.log('[ENGINE] Loading assets');
        
        // Create a fallback texture for missing assets
        const graphics = new Graphics();
        graphics.beginFill(0xFF0000);
        graphics.drawRect(0, 0, 32, 32);
        graphics.endFill();
        const fallbackTexture = this.app.renderer.generateTexture(graphics);
        graphics.destroy();

        // Initialize resources object
        this.resources = {};

        // Get asset paths from AssetManager's ASSET_BUNDLES
        const assetPaths = Object.values(ASSET_BUNDLES.images.assets);

        // Try to load each asset individually
        for (const path of assetPaths) {
            try {
                const texture = await Assets.load(path);
                const shortName = path.split('/').pop().split('.')[0];
                this.resources[shortName] = texture;
                console.log(`[ENGINE] Successfully loaded asset: ${path}`);
            } catch (err) {
                console.warn(`[ENGINE] Failed to load asset: ${path}, using fallback texture`);
                const shortName = path.split('/').pop().split('.')[0];
                this.resources[shortName] = fallbackTexture;
            }
        }

        // Load sprites into AssetManager
        if (this.assetManager) {
            console.log('[ENGINE] Loading sprites into AssetManager');
            this.assetManager.loadSprites();
        } else {
            console.warn('[ENGINE] No AssetManager available to load sprites');
        }
    }

    async initializeGameWorld() {
        console.log('[ENGINE] Initializing game world');
        // Create ECS world
        this.world = createWorld();
        this.world.entities = [];
        this.world.sprites = {};
        this.world.showHeightmap = false;
        this.world.showBiome = false;
        this.world.isPaused = false;
        this.world.selectedEntity = null;
        this.world.selectedEntityType = null;
        this.world.mapGenerated = false;
        this.world.resources = this.resources;

        // Initialize render system first
        console.log('[ENGINE] Initializing render system');
        // Create and store the RenderSystem instance
        this.renderSystem = initRenderSystem(this.app, this.world, this.resources); 

        // Reset tile manager
        console.log('[ENGINE] Resetting tile manager');
        tileManager.reset();
        
        // IMPORTANT: Make sure we initialize the app reference in hive and bee systems
        // before generating any structures
        console.log('[ENGINE] Initializing hive and bee systems with app reference');
        console.log('[ENGINE] App reference:', this.app ? 'valid' : 'null/undefined');
        console.log('[ENGINE] App renderer:', this.app && this.app.renderer ? 'available' : 'missing');
        console.log('[ENGINE] World container:', this.app && this.app.worldContainer ? 'available' : 'missing');
        
        setHiveApp(this.app);
        setApp(this.app);
        setBeeSystemApp(this.app);
        
        // Initialize the world
        initializeWorld(this.world);
        
        // Apply custom spawn rules to control which entities can spawn
        try {
            console.log('[ENGINE] Applying custom entity spawn configuration');
            // Import and apply the spawn configuration
            // const spawnConfig = await import('../spawn-config.js');
            // Spawn config automatically applies itself on import
            console.log('[ENGINE] Custom entity spawn configuration skipped (disabled for debugging)');
        } catch (error) {
            console.warn('[ENGINE] Failed to apply custom spawn configuration:', error);
        }

        // Generate map
        console.log('[ENGINE] Generating map');
        mapGenerationSystem(this.world);
        console.log('[ENGINE] Map generated, total entities:', this.world.entities.length);

        // Generate plants
        console.log('[ENGINE] Generating plants');
        plantGenerationSystem(this.world);
        console.log('[ENGINE] Plants generated');
        
        // Generate rocks
        console.log('[ENGINE] Generating rocks');
        rockGenerationSystem(this.world);
        console.log('[ENGINE] Rocks generated');
        
        // Generate structures
        console.log('[ENGINE] Generating structures');
        structureGenerationSystem(this.world);
        console.log('[ENGINE] Structures generated');

        // Generate animals
        console.log('[ENGINE] Generating animals');
        animalGenerationSystem(this.world);
        console.log('[ENGINE] Animals generated');

        // Initialize world with bee eaters
        console.log('[ENGINE] Initializing world with bee eaters');
        initializeWorld(this.world);
        console.log('[ENGINE] World initialized with bee eaters');

        // Initialize time system
        console.log('[ENGINE] Initializing time system');
        this.world.timeEntity = createTimeSystem(this.world);

        // Create player
        console.log('[ENGINE] Creating player');
        const player = addEntity(this.world);
        this.world.entities.push(player);
        addComponent(this.world, Position, player);
        addComponent(this.world, Velocity, player);
        addComponent(this.world, Movement, player);
        addComponent(this.world, Input, player);
        addComponent(this.world, SpriteComponent, player);
        addComponent(this.world, Health, player);
        addComponent(this.world, Player, player);
        Player[player] = 1;
        addComponent(this.world, Inventory, player);
        addComponent(this.world, Wetness, player);
        addComponent(this.world, Hunger, player);
        addComponent(this.world, Thirst, player);
        addComponent(this.world, SoundLevel, player);

        // Initialize player components
        Position.x[player] = (MAP_WIDTH / 2) * TILE_SIZE;
        Position.y[player] = (MAP_HEIGHT / 2) * TILE_SIZE;
        Velocity.x[player] = 0;
        Velocity.y[player] = 0;
        Movement.speed[player] = PLAYER_SPEED;
        Movement.acceleration[player] = PLAYER_ACCELERATION;
        Movement.deceleration[player] = PLAYER_DECELERATION;
        Movement.maxSpeed[player] = PLAYER_MAX_SPEED;
        Movement.isSwimming[player] = 0;
        Movement.isWalking[player] = 0;
        
        // Set player stats from PLAYER_STATS configuration
        Health.value[player] = PLAYER_STATS.INITIAL_HEALTH;
        Inventory.wood[player] = 0;
        Inventory.food[player] = 0;
        Inventory.tools[player] = 0;
        
        // Wetness
        Wetness.value[player] = PLAYER_STATS.INITIAL_WETNESS;
        Wetness.dryingRate[player] = PLAYER_STATS.DRY_RATE;
        Wetness.wettingRate[player] = PLAYER_STATS.WETNESS_RATE;
        
        // Hunger and thirst - using dynamic rates from PLAYER_STATS
        Hunger.value[player] = PLAYER_STATS.INITIAL_HUNGER;
        Thirst.value[player] = PLAYER_STATS.INITIAL_THIRST;
        
        // We don't need to set drainRate since we'll use the dynamic rates in the systems
        // but setting to a very small value in case any code still uses these
        Hunger.drainRate[player] = 0.00001; 
        Thirst.drainRate[player] = 0.00001;
        
        Thirst.canDrink[player] = 0;
        
        // Sound system
        SoundLevel.value[player] = 0;
        SoundLevel.decayRate[player] = 0.5;
        SoundLevel.isMoving[player] = 0;
        SoundLevel.isRunning[player] = 0;

        // Initialize lighting system if enabled
        if (CURRENT_MODE.lighting) {
            this.lightingSystem = new LightingSystem();
            this.world.lightingSystem = this.lightingSystem;
            console.log('[ENGINE] Lighting system initialized');
        } else {
            this.world.lightingSystem = null;
            console.log('[ENGINE] Lighting system disabled for this game mode');
        }

        // Initialize hive and bee systems
        console.log('[ENGINE] Initializing hive and bee systems');
        setHiveApp(this.app);
        setApp(this.app); // Set app for bee system

        // Add bee system to game systems
        this.systems.set('bee', beeSystem);

        // Initialize systems
        await this.initializeSystems();
        
        // Ensure we're using the render system's queries
        if (this.renderSystem && this.renderSystem.getQueries) {
            console.log('[ENGINE] Setting world queries from render system');
            this.world.queries = this.renderSystem.getQueries();
        }

        // Only create a hive if hives are enabled in game mode
        if (CURRENT_MODE.hives && shouldSpawnEntityType('HIVE', 'structures')) {
            console.log('[ENGINE] Creating initial hive');
            const playerTileX = Math.floor(Position.x[player] / TILE_SIZE);
            const playerTileY = Math.floor(Position.y[player] / TILE_SIZE);
            
            // Place hive 5 tiles away from player
            const hiveTileX = playerTileX + 2;
            const hiveTileY = playerTileY - 2;
            
            // Convert to world coordinates (center of tile)
            const hiveX = (hiveTileX * TILE_SIZE) + (TILE_SIZE / 2);
            const hiveY = (hiveTileY * TILE_SIZE) + (TILE_SIZE / 2);
            
            console.log(`[ENGINE] Placing hive at tile (${hiveTileX}, ${hiveTileY}), world coords (${hiveX}, ${hiveY})`);
            const hiveId = createHive(this.world, hiveX, hiveY);
            console.log(`[ENGINE] Hive created with ID: ${hiveId}`);
        } else {
            console.log('[ENGINE] Skipping initial hive creation (disabled in configuration)');
        }

        // Show world container
        this.worldContainer.visible = true;

        // Add the wander system to the game systems
        this.systems.set('wander', wanderSystem);

        console.log('[ENGINE] Game world initialized');

        // Only create bee-eaters if bees are enabled
        if (CURRENT_MODE.bees && shouldSpawnEntityType('BEE_EATER', 'animals')) {
            console.log('[ENGINE] Creating initial bee-eaters');
            createBeeEater(this.world, 10, 10); // First bee eater
            createBeeEater(this.world, 15, 15); // Second bee eater
        } else {
            console.log('[ENGINE] Skipping bee eater creation (disabled in configuration)');
        }

        // Manually spawn two rabbits using the rabbit module
        console.log('[ENGINE] Manually spawning rabbits');
        
        // Helper function to find a valid land position
        const findValidLandPosition = () => {
            const maxAttempts = 100; // Try up to 100 times
            for (let i = 0; i < maxAttempts; i++) {
                const tileX = Math.floor(Math.random() * MAP_WIDTH);
                const tileY = Math.floor(Math.random() * MAP_HEIGHT);
                const tile = tileManager.getTile(tileX, tileY);
                if (tile && tile.type === TILE_TYPES.LAND) {
                    // Convert tile coordinates to world coordinates
                    const worldX = tileX * TILE_SIZE + (TILE_SIZE / 2);
                    const worldY = tileY * TILE_SIZE + (TILE_SIZE / 2);
                    return { x: worldX, y: worldY };
                }
            }
            return null;
        };
        
        // Find valid positions for rabbits
        const pos1 = findValidLandPosition();
        const pos2 = findValidLandPosition();
        
        if (pos1 && pos2) {
            // Create rabbits using the rabbit module
            const rabbit1 = createRabbit(this.world, pos1.x, pos1.y);
            const rabbit2 = createRabbit(this.world, pos2.x, pos2.y);
            
            console.log('[ENGINE] Rabbits spawned at positions:', 
                `(${pos1.x}, ${pos1.y}) and (${pos2.x}, ${pos2.y})`);
        } else {
            console.error('[ENGINE] Failed to find valid land positions for rabbits');
        }
    }

    async initializeSystems() {
        console.log('[ENGINE] Initializing systems');
        // Add all game systems
        this.systems.set('movement', movementSystem);
        this.systems.set('wetness', wetnessSystem);
        this.systems.set('time', timeSystem);
        this.systems.set('plants', plantGrowthSystem);
        this.systems.set('animals', animalBehaviorSystem);
        this.systems.set('render', this.renderSystem);
        this.systems.set('health', healthSystem);
        this.systems.set('hunger', hungerSystem);
        this.systems.set('thirst', thirstSystem);
        
        // Initialize simulation system for ecosystem testing
        console.log('[ENGINE] Initializing simulation system...');
        this.simulationSystem = new SimulationSystem(this.world, this.app);
        this.systems.set('simulation', this.simulationSystem);
        
        // Pass gameplay container to app
        this.app.gameplayContainer = this.gameplayContainer;
        
        // Create UI system
        this.uiSystem = createUISystem(this.app);
        this.systems.set('ui', this.uiSystem);
        
        // Store reference to UI container
        this.uiContainer = this.gameplayContainer.children.find(child => 
            child.constructor.name === 'Container' && 
            child.children.some(c => c.constructor.name === 'Sprite' && c.texture && c.texture.baseTexture.resource)
        );

        console.log('[ENGINE] Systems initialized');
    }

    startGameLoop() {
        console.log('[ENGINE] Game loop started');
        this.isRunning = true;
        // Use PIXI's ticker for the game loop
        this.app.ticker.add(this.gameLoop, this); 
    }

    stop() {
        console.log('[ENGINE] Stopping game loop');
        this.isRunning = false;
        this.app.ticker.remove(this.gameLoop, this);
        // Add any other cleanup needed when stopping temporarily
    }

    destroy() {
        console.log('[ENGINE] Destroying engine');
        this.stop(); // Ensure game loop is stopped

        // Clean up any animation tickers on sprites
        if (this.world && this.world.sprites) {
            console.log('[ENGINE] Cleaning up sprite animations');
            for (const [eid, sprite] of Object.entries(this.world.sprites)) {
                if (sprite && sprite.animate && this.app && this.app.ticker) {
                    console.log(`[ENGINE] Removing animation ticker from sprite for entity #${eid}`);
                    this.app.ticker.remove(sprite.animate);
                }
            }
        }

        // Cleanup render system instance
        if (this.renderSystem && typeof this.renderSystem.cleanup === 'function') {
            this.renderSystem.cleanup();
        }

        // Destroy PIXI application
        if (this.app) {
            this.app.destroy(true, { children: true, texture: true, baseTexture: true });
            this.app = null;
        }

        // Remove event listeners
        // window.removeEventListener('resize', handleResize); // Need reference to remove specific listener
        // window.removeEventListener('keydown', ...);
        // window.removeEventListener('keyup', ...);

        // Clear world and other properties
        this.world = null;
        this.worldContainer = null;
        this.gameplayContainer = null;
        this.menuSystem = null;
        this.uiManager = null;
        this.resources = null;
        this.systems.clear();
        this.gameComponents.clear();
        this.uiContainer = null;
        this.uiSystem = null;
        this.renderSystem = null;
        this.lightingSystem = null;
        this.assetManager = null; // Don't destroy asset manager here, might be shared

        console.log('[ENGINE] Engine destroyed');
    }

    // Add a game loop execution step
    gameLoop(delta) {
        if (!this.world || this.world.isPaused) return;

        // Performance Timing Start
        const frameStartTime = performance.now();
        const systemTimings = {}; // Store timings for this frame

        // Initialize tick counter if not set
        if (this.world.tick === undefined) {
            this.world.tick = 0;
        }

        // Increment tick counter
        this.world.tick++;

        // --- Update Systems ---
        let startTime, endTime;
        
        // Get player position for performance optimization
        const players = playerQuery(this.world);
        let playerX = 0, playerY = 0;
        if (players.length > 0) {
            playerX = Position.x[players[0]];
            playerY = Position.y[players[0]];
        }
        
        startTime = performance.now();
        timeSystem(this.world);
        endTime = performance.now();
        systemTimings.time = (endTime - startTime).toFixed(2);

        startTime = performance.now();
        movementSystem(this.world);
        endTime = performance.now();
        systemTimings.movement = (endTime - startTime).toFixed(2);
        
        startTime = performance.now();
        aiSystem(this.world, delta);
        endTime = performance.now();
        systemTimings.ai = (endTime - startTime).toFixed(2);
        
        // Update ecosystem system with performance optimization
        startTime = performance.now();
        ecosystemSystem.update(this.world, delta, playerX, playerY);
        endTime = performance.now();
        systemTimings.ecosystem = (endTime - startTime).toFixed(2);
        
        // Update balanced spawner periodically
        if (this.world.tick % 600 === 0) { // Every 10 seconds at 60 FPS
            startTime = performance.now();
            balancedSpawner.balancePopulations(this.world);
            endTime = performance.now();
            systemTimings.spawning = (endTime - startTime).toFixed(2);
        }
        
        startTime = performance.now();
        beeSystem(this.world, delta);
        endTime = performance.now();
        systemTimings.bee = (endTime - startTime).toFixed(2);

        startTime = performance.now();
        plantGrowthSystem(this.world);
        endTime = performance.now();
        systemTimings.plantGrowth = (endTime - startTime).toFixed(2);

        startTime = performance.now();
        animalBehaviorSystem(this.world, delta);
        endTime = performance.now();
        systemTimings.animalBehavior = (endTime - startTime).toFixed(2);
        
        // Update stats systems
        startTime = performance.now();
        hungerSystem(this.world);
        thirstSystem(this.world);
        healthSystem(this.world);
        wetnessSystem(this.world);
        endTime = performance.now();
        systemTimings.stats = (endTime - startTime).toFixed(2);

        // Update lighting system (if enabled)
        startTime = performance.now();
        this.lightingSystem?.update(this.worldContainer, this.world);
        endTime = performance.now();
        systemTimings.lighting = (endTime - startTime).toFixed(2);
        
        // Store timings in world state for potential HUD display
        this.world.systemTimings = systemTimings;
        
        // Adaptive performance adjustment
        const adaptivePerfStartTime = performance.now();
        const adaptivePerfTime = adaptivePerfStartTime - frameStartTime;
        performanceOptimizer.adjustQuality(adaptivePerfTime);

        // --- Render Frame ---
        const renderStartTime = performance.now();
        if (this.renderSystem) {
            this.renderSystem.update(); 
        } else {
            console.error("[ENGINE] RenderSystem instance not available for update.");
        }
        const renderEndTime = performance.now();
        systemTimings.render = (renderEndTime - renderStartTime).toFixed(2);
        
        // Performance Timing End
        const finalFrameTime = performance.now();
        const totalFrameTime = (finalFrameTime - frameStartTime).toFixed(2);
        
        // Optional: Log timings every N frames to avoid console spam
        if (this.world.tick % 60 === 0) { // Log every 60 ticks (roughly once per second at 60fps)
            console.log(`[Perf] Frame ${this.world.tick} Total: ${totalFrameTime}ms | Timings:`, systemTimings);
        }
    }

    // Method to manually place a hive at a specific location
    placeHive(x, y) {
        // Convert to tile coordinates if world coordinates were provided
        let tileX, tileY;
        if (x > TILE_SIZE || y > TILE_SIZE) {
            // Input is likely world coordinates, convert to tile
            tileX = Math.floor(x / TILE_SIZE);
            tileY = Math.floor(y / TILE_SIZE);
        } else {
            // Input is already tile coordinates
            tileX = Math.floor(x);
            tileY = Math.floor(y);
        }
        
        // Convert to world coordinates (center of tile)
        const worldX = (tileX * TILE_SIZE) + (TILE_SIZE / 2);
        const worldY = (tileY * TILE_SIZE) + (TILE_SIZE / 2);
        
        console.log(`[ENGINE] Manually placing hive at tile (${tileX}, ${tileY}), world coords (${worldX}, ${worldY})`);
        try {
            const hive = createHive(this.world, worldX, worldY);
            if (hive) {
                console.log(`[ENGINE] Successfully placed hive with ID: ${hive}`);
                // Update entity counts
                updateEntityCount('HIVE', 'structures', 1, true);
                return hive;
            } else {
                console.error('[ENGINE] Failed to place hive');
                return null;
            }
        } catch (error) {
            console.error('[ENGINE] Error placing hive:', error);
            return null;
        }
    }

    // Method to place a hive directly in front of the player
    placeHiveNearPlayer(distanceInTiles = 3) {
        try {
            const players = playerQuery(this.world);
            if (players.length === 0) {
                console.error('[ENGINE] No player found');
                return null;
            }
            
            const playerId = players[0];
            
            // Get player tile position
            const playerTileX = Math.floor(Position.x[playerId] / TILE_SIZE);
            const playerTileY = Math.floor(Position.y[playerId] / TILE_SIZE);
            
            // Place hive in front of player (North/Up direction) by specified number of tiles
            const hiveTileX = playerTileX;
            const hiveTileY = playerTileY - distanceInTiles;
            
            // Convert to world coordinates (center of tile)
            const hiveX = (hiveTileX * TILE_SIZE) + (TILE_SIZE / 2);
            const hiveY = (hiveTileY * TILE_SIZE) + (TILE_SIZE / 2);
            
            console.log(`[ENGINE] Placing hive near player at tile (${hiveTileX}, ${hiveTileY}), world coords (${hiveX}, ${hiveY})`);
            const hiveId = createHive(this.world, hiveX, hiveY);
            
            if (hiveId) {
                console.log(`[ENGINE] Successfully placed hive with ID: ${hiveId}`);
                // Update entity counts
                updateEntityCount('HIVE', 'structures', 1, true);
                return hiveId;
            } else {
                console.error('[ENGINE] Failed to place hive');
                return null;
            }
        } catch (error) {
            console.error('[ENGINE] Error placing hive near player:', error);
            return null;
        }
    }

    // Debug method to find problems with hive/structure rendering
    debugHiveStructure() {
        try {
            const players = playerQuery(this.world);
            if (players.length === 0) {
                console.error('[DEBUG-HIVE] No player found');
                return null;
            }
            
            // Place a hive at a fixed location
            const hiveX = 740;
            const hiveY = 540;
            
            console.log('[DEBUG-HIVE] Creating test hive at fixed location:', { x: hiveX, y: hiveY });
            
            // Create the hive
            const hiveId = createHive(this.world, hiveX, hiveY);
            console.log('[DEBUG-HIVE] Hive created with ID:', hiveId);
            
            // Verify its components
            if (hiveId) {
                console.log('[DEBUG-HIVE] Structure component is imported:', !!Structure);
                console.log('[DEBUG-HIVE] Structure type on entity:', Structure.type[hiveId]);
                console.log('[DEBUG-HIVE] Position on entity:', {
                    x: Position.x[hiveId], 
                    y: Position.y[hiveId]
                });
                
                // Check if it appears in the structures query
                const structureEntities = this.world.queries.structures(this.world);
                console.log('[DEBUG-HIVE] Total structure entities found:', structureEntities.length);
                console.log('[DEBUG-HIVE] Is our new hive in the structures query?', 
                    structureEntities.includes(hiveId));
                
                // Check how it would be identified
                const { entityType } = getEntityTypeInfo(this.world, hiveId);
                console.log('[DEBUG-HIVE] Entity type identified as:', entityType);
                
                // Call the sprite debugging method
                this.debugHiveSprite(hiveId);
                
                return hiveId;
            } else {
                console.error('[DEBUG-HIVE] Failed to create hive');
                return null;
            }
        } catch (error) {
            console.error('[DEBUG-HIVE] Error debugging hive structure:', error);
            return null;
        }
    }
    
    // Debug method to specifically check sprite issues
    debugHiveSprite(hiveId) {
        try {
            console.log('[DEBUG-SPRITE] Checking sprite for hive #' + hiveId);
            
            // Verify sprite exists in world.sprites
            if (this.world.sprites && this.world.sprites[hiveId]) {
                const sprite = this.world.sprites[hiveId];
                console.log('[DEBUG-SPRITE] Sprite found in world.sprites:', {
                    width: sprite.width,
                    height: sprite.height,
                    visible: sprite.visible,
                    alpha: sprite.alpha,
                    position: `(${sprite.x}, ${sprite.y})`,
                    zIndex: sprite.zIndex,
                    hasParent: !!sprite.parent,
                    parentName: sprite.parent ? (sprite.parent.name || 'unnamed') : 'none'
                });
                
                // Check if parent container is visible
                if (sprite.parent) {
                    console.log('[DEBUG-SPRITE] Parent container:', {
                        visible: sprite.parent.visible,
                        alpha: sprite.parent.alpha,
                        position: `(${sprite.parent.x}, ${sprite.parent.y})`,
                        scale: `(${sprite.parent.scale.x}, ${sprite.parent.scale.y})`,
                        childCount: sprite.parent.children.length
                    });
                    
                    // Check if sprite is in parent's children
                    const index = sprite.parent.children.indexOf(sprite);
                    console.log(`[DEBUG-SPRITE] Sprite is child #${index} in parent container`);
                    
                    // Check if worldContainer is related to sprite's parent
                    const isParentTheWorldContainer = sprite.parent === this.worldContainer;
                    const isParentInWorldContainer = this.worldContainer.children.includes(sprite.parent);
                    console.log('[DEBUG-SPRITE] Container relationships:', {
                        isParentTheWorldContainer,
                        isParentInWorldContainer,
                        worldContainerChildCount: this.worldContainer.children.length
                    });
                    
                    // IMPORTANT: Check if sprite is actually rendered by the entity renderer
                    const entityRenderer = this.renderSystem?.entityRenderer;
                    if (entityRenderer) {
                        const isManagedByRenderer = entityRenderer.sprites.has(hiveId);
                        console.log('[DEBUG-SPRITE] Managed by EntityRenderer:', isManagedByRenderer);
                        
                        if (isManagedByRenderer) {
                            const rendererSprite = entityRenderer.sprites.get(hiveId);
                            console.log('[DEBUG-SPRITE] EntityRenderer is using a different sprite than world.sprites:',
                                rendererSprite !== sprite);
                        }
                    }
                }
            } else {
                console.error('[DEBUG-SPRITE] No sprite found in world.sprites for entity #' + hiveId);
            }
        } catch (error) {
            console.error('[DEBUG-SPRITE] Error checking sprite:', error);
        }
    }

    // Debug function to force spawn bees from an existing hive
    debugBeeSpawning() {
        try {
            console.log('[DEBUG] Testing bee spawning...');
            
            // Find all hives in the world
            const hives = [];
            for (let eid = 1; eid < 10000; eid++) {
                if (Structure.type && Structure.type[eid] === 1) {
                    hives.push(eid);
                }
            }
            
            if (hives.length === 0) {
                console.error('[DEBUG] No hives found in the world. Creating a new hive...');
                
                // Place a hive near player
                const newHive = this.placeHiveNearPlayer(2);
                if (newHive) {
                    hives.push(newHive);
                    console.log(`[DEBUG] Created new hive #${newHive}`);
                } else {
                    console.error('[DEBUG] Failed to create a new hive');
                    return "Failed to find or create hives";
                }
            }
            
            // For each hive, spawn some bees and verify the components
            let totalSpawned = 0;
            
            for (const hive of hives) {
                console.log(`[DEBUG] Testing bee spawning for hive #${hive}`);
                
                // Check that hive has Production component
                if (!Production.type || Production.type[hive] === undefined) {
                    console.error(`[DEBUG] Hive #${hive} is missing Production component`);
                    
                    // Try to add it
                    console.log(`[DEBUG] Adding missing Production component to hive #${hive}`);
                    addComponent(this.world, Production, hive);
                    Production.type[hive] = 1; // 1 for honey
                    Production.amount[hive] = 0;
                    Production.rate[hive] = 0.1;
                    Production.maxAmount[hive] = 100;
                }
                
                // Try to spawn 3 bees directly
                console.log(`[DEBUG] Attempting to spawn 3 bees for hive #${hive}...`);
                const bees = [];
                
                for (let i = 0; i < 3; i++) {
                    const hiveX = Position.x[hive];
                    const hiveY = Position.y[hive];
                    
                    const angle = (i / 3) * Math.PI * 2;
                    const distance = 32; // Spawn 32 units away from hive
                    const beeX = hiveX + Math.cos(angle) * distance;
                    const beeY = hiveY + Math.sin(angle) * distance;
                    
                    // Pass Position component explicitly
                    const bee = createBee(this.world, beeX, beeY, hive, Position);
                    if (bee) {
                        bees.push(bee);
                        console.log(`[DEBUG] Spawned bee #${bee} at (${beeX.toFixed(1)}, ${beeY.toFixed(1)})`);
                        totalSpawned++;
                    }
                }
                
                console.log(`[DEBUG] Spawned ${bees.length} bees for hive #${hive}`);
                
                // Reset spawn timer for the hive
                console.log(`[DEBUG] Resetting hive #${hive} spawn timer`);
                hive.lastBeeSpawn = 0;
            }
            
            return `Successfully spawned ${totalSpawned} bees for ${hives.length} hives`;
        } catch (error) {
            console.error('[DEBUG] Error testing bee spawning:', error);
            return `Error: ${error.message}`;
        }
    }

    // Debug function to make all bees move
    forceBeesMove() {
        try {
            console.log('[DEBUG] Forcing all bees to move...');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.log('[DEBUG] No bees found to move');
                return "No bees found";
            }
            
            console.log(`[DEBUG] Found ${bees.length} bees to move`);
            
            // Force each bee to MOVING state with random target
            for (const bee of bees) {
                // Get current position
                const beeX = Position.x[bee];
                const beeY = Position.y[bee];
                
                // TELEPORT bees to completely new locations (multiple tiles away)
                // This is an extreme measure to ensure they're visibly moving
                const angle = Math.random() * Math.PI * 2;
                const distance = TILE_SIZE * (10 + Math.random() * 20); // Move 10-30 tiles away!
                
                // Calculate new target position
                const newTargetX = beeX + Math.cos(angle) * distance;
                const newTargetY = beeY + Math.sin(angle) * distance;
                
                // IMMEDIATELY jump position by several tiles
                const jumpDistance = TILE_SIZE * (3 + Math.random() * 5); // Jump 3-8 tiles immediately
                Position.x[bee] = beeX + Math.cos(angle) * jumpDistance;
                Position.y[bee] = beeY + Math.sin(angle) * jumpDistance;
                
                // Set target for continued movement
                AI.targetX[bee] = newTargetX;
                AI.targetY[bee] = newTargetY;
                
                // Force state to MOVING
                AI.state[bee] = AI_STATE.MOVING;
                Animal.isMoving[bee] = 1;
                
                console.log(`[DEBUG] TELEPORTED bee #${bee} from (${beeX.toFixed(1)}, ${beeY.toFixed(1)}) to (${Position.x[bee].toFixed(1)}, ${Position.y[bee].toFixed(1)})`);
            }
            
            // Force immediate render update
            if (this.renderSystem) {
                this.renderSystem.update();
            }
            
            return `Dramatically moved ${bees.length} bees to new locations`;
        } catch (error) {
            console.error('[DEBUG] Error forcing bees to move:', error);
            return `Error: ${error.message}`;
        }
    }

    // Debug function to force bee animations
    forceBeesAnimate() {
        try {
            console.log('[DEBUG] Forcing all bee animations...');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.log('[DEBUG] No bees found to animate');
                return "No bees found";
            }
            
            console.log(`[DEBUG] Found ${bees.length} bees to animate`);
            
            // Force each bee to animate
            let animatedCount = 0;
            for (const bee of bees) {
                const sprite = this.world.sprites[bee];
                if (sprite) {
                    // Remove existing animation if any
                    if (sprite.animate && this.app && this.app.ticker) {
                        this.app.ticker.remove(sprite.animate);
                    }
                    
                    // Set up animation properties with more dramatic values
                    sprite.animationSpeed = Math.random() * 0.08 + 0.04; // Even faster animations
                    sprite.oscillationRange = Math.random() * 60 + 40; // Extra large oscillation range
                    sprite.oscillationPhase = Math.random() * Math.PI * 2;
                    sprite.originX = Position.x[bee];
                    sprite.originY = Position.y[bee];
                    sprite.lastUpdateTime = Date.now();
                    
                    // Add animation function
                    sprite.animate = () => {
                        const now = Date.now();
                        const deltaTime = now - (sprite.lastUpdateTime || now);
                        sprite.lastUpdateTime = now;
                        
                        // Calculate oscillation
                        sprite.oscillationPhase += sprite.animationSpeed * (deltaTime / 16.67);
                        
                        // Update sprite position with oscillation
                        const offsetX = Math.cos(sprite.oscillationPhase) * sprite.oscillationRange;
                        const offsetY = Math.sin(sprite.oscillationPhase * 1.5) * (sprite.oscillationRange / 2);
                        
                        // Apply to sprite's position
                        sprite.position.x = Position.x[bee] + offsetX;
                        sprite.position.y = Position.y[bee] + offsetY;
                        
                        // Apply visual rotation
                        sprite.rotation = Math.sin(sprite.oscillationPhase * 0.5) * 0.5; // More rotation
                    };
                    
                    // Add to ticker
                    this.app.ticker.add(sprite.animate);
                    animatedCount++;
                    
                    console.log(`[DEBUG] Added animation to bee #${bee}`);
                }
            }
            
            // Force immediate render update
            if (this.renderSystem) {
                this.renderSystem.update();
            }
            
            return `Added animations to ${animatedCount} bees`;
        } catch (error) {
            console.error('[DEBUG] Error forcing bee animations:', error);
            return `Error: ${error.message}`;
        }
    }

    // Extreme debug function to constantly teleport bees around the map
    teleportBees() {
        try {
            console.log('[DEBUG] Setting up continuous bee teleportation...');
            
            // First do an immediate teleport
            this.forceBeesMove();
            
            // Set an interval to keep moving bees every second
            if (this._beeInterval) {
                clearInterval(this._beeInterval);
            }
            
            this._beeInterval = setInterval(() => {
                // Get all bee entities
                const bees = this.world.queries.animals(this.world).filter(bee => 
                    Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
                );
                
                if (bees.length === 0) {
                    return;
                }
                
                // For each bee, make a dramatic position change
                for (const bee of bees) {
                    // Choose a completely random position on the map
                    const randomX = Math.random() * (MAP_WIDTH * TILE_SIZE);
                    const randomY = Math.random() * (MAP_HEIGHT * TILE_SIZE);
                    
                    // Directly teleport the bee
                    Position.x[bee] = randomX;
                    Position.y[bee] = randomY;
                    
                    // Also update target
                    AI.targetX[bee] = randomX + (Math.random() * TILE_SIZE * 10 - TILE_SIZE * 5);
                    AI.targetY[bee] = randomY + (Math.random() * TILE_SIZE * 10 - TILE_SIZE * 5);
                }
                
                // Force render update
                if (this.renderSystem) {
                    this.renderSystem.update();
                }
                
                console.log(`[DEBUG] Teleported ${bees.length} bees to random locations`);
            }, 1000); // Every second
            
            return "Continuous bee teleportation activated - bees will jump around the map every second!";
        } catch (error) {
            console.error('[DEBUG] Error setting up bee teleportation:', error);
            return `Error: ${error.message}`;
        }
    }
    
    // Stop teleporting bees
    stopTeleportingBees() {
        if (this._beeInterval) {
            clearInterval(this._beeInterval);
            this._beeInterval = null;
            return "Bee teleportation stopped";
        }
        return "No active bee teleportation to stop";
    }

    // Critical fix to ensure bee positions are correctly updated and they leave their tiles
    forceFixBeePositions() {
        try {
            console.log('[DEBUG] CRITICAL BEE POSITION FIX RUNNING');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.log('[DEBUG] No bees found to fix positions');
                return "No bees found";
            }
            
            console.log(`[DEBUG] Found ${bees.length} bees to fix positions`);
            
            // For each bee sprite, REMOVE the animation ticker and DIRECTLY set position
            for (const bee of bees) {
                const sprite = this.world.sprites[bee];
                if (sprite) {
                    // Remove the animation ticker that's adding oscillation
                    if (sprite.animate && this.app && this.app.ticker) {
                        console.log(`[DEBUG] Removing animation ticker from bee #${bee}`);
                        this.app.ticker.remove(sprite.animate);
                        sprite.animate = null;
                    }
                    
                    // Explicitly teleport both sprite and entity position
                    const randomX = Math.random() * (MAP_WIDTH * TILE_SIZE);
                    const randomY = Math.random() * (MAP_HEIGHT * TILE_SIZE);
                    
                    // Set the entity position
                    Position.x[bee] = randomX;
                    Position.y[bee] = randomY;
                    
                    // DIRECTLY set sprite position with NO OFFSETS
                    sprite.position.x = randomX;
                    sprite.position.y = randomY;
                    
                    console.log(`[DEBUG] FORCE-FIXED bee #${bee} position to (${randomX.toFixed(1)}, ${randomY.toFixed(1)})`);
                } else {
                    console.warn(`[DEBUG] No sprite found for bee #${bee}`);
                }
            }
            
            // Force sprite update even if the render system is stuck
            if (this.renderSystem) {
                this.renderSystem.update();
            }
            
            return `Successfully force-fixed positions for ${bees.length} bees. They should now be completely teleported to random locations.`;
        } catch (error) {
            console.error('[DEBUG] Error fixing bee positions:', error);
            return `Error: ${error.message}`;
        }
    }

    // Force all bees to move upward
    beesGoUp() {
        try {
            console.log('[DEBUG] Making all bees go UP');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.log('[DEBUG] No bees found to move upward');
                return "No bees found";
            }
            
            console.log(`[DEBUG] Found ${bees.length} bees to move upward`);
            
            // For each bee, set a target position far above current position
            for (const bee of bees) {
                const beeX = Position.x[bee];
                const beeY = Position.y[bee];
                
                // Set target very far up (10-20 tiles up)
                const moveDistance = TILE_SIZE * (10 + Math.random() * 10);
                
                // Update positions directly - immediate jump by 2 tiles
                Position.x[bee] = beeX;
                Position.y[bee] = beeY - TILE_SIZE * 2;
                
                // Set target for continued upward movement
                AI.targetX[bee] = beeX; // Same X coordinate
                AI.targetY[bee] = beeY - moveDistance; // Far up the screen
                
                // Force state to MOVING
                AI.state[bee] = AI_STATE.MOVING;
                Animal.isMoving[bee] = 1;
                
                // Update the sprite position directly
                const sprite = this.world.sprites[bee];
                if (sprite) {
                    sprite.position.set(Position.x[bee], Position.y[bee]);
                }
                
                console.log(`[DEBUG] Bee #${bee} moving UP from (${beeX}, ${beeY}) to (${beeX}, ${beeY-moveDistance})`);
            }
            
            // Force immediate render update
            if (this.renderSystem) {
                this.renderSystem.update();
            }
            
            return `Commanded ${bees.length} bees to fly upward!`;
        } catch (error) {
            console.error('[DEBUG] Error making bees go up:', error);
            return `Error: ${error.message}`;
        }
    }

    // Make bees form a V formation and fly upward
    beesFormationUp() {
        try {
            console.log('[DEBUG] Making bees form V formation and fly upward');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.log('[DEBUG] No bees found for formation');
                return "No bees found";
            }
            
            console.log(`[DEBUG] Found ${bees.length} bees for V formation`);
            
            // Find player position as reference point
            const players = this.world.queries.players(this.world);
            let centerX = MAP_WIDTH * TILE_SIZE / 2;
            let centerY = MAP_HEIGHT * TILE_SIZE / 2;
            
            if (players.length > 0) {
                const player = players[0];
                centerX = Position.x[player];
                centerY = Position.y[player];
            }
            
            // Position all bees initially in a row below the player
            const startY = centerY + TILE_SIZE * 10; // Start 10 tiles below player
            const startX = centerX;
            const spacing = TILE_SIZE * 1.5; // Space between bees in formation
            
            // Calculate positions for V formation
            for (let i = 0; i < bees.length; i++) {
                const bee = bees[i];
                
                // Alternating positions for V shape
                const isLeft = i % 2 === 0;
                const positionInWing = Math.floor(i / 2);
                
                // Calculate position in V formation
                let formationX;
                if (isLeft) {
                    formationX = startX - spacing * (positionInWing + 1);
                } else {
                    formationX = startX + spacing * positionInWing;
                }
                
                const formationY = startY + positionInWing * spacing * 0.5;
                
                // Set bee position
                Position.x[bee] = formationX;
                Position.y[bee] = formationY;
                
                // Set target position far above (keep V formation)
                AI.targetX[bee] = formationX;
                AI.targetY[bee] = formationY - TILE_SIZE * 50; // 50 tiles up
                
                // Force state to MOVING
                AI.state[bee] = AI_STATE.MOVING;
                Animal.isMoving[bee] = 1;
                
                // Update the sprite position directly
                const sprite = this.world.sprites[bee];
                if (sprite) {
                    sprite.position.set(Position.x[bee], Position.y[bee]);
                }
                
                console.log(`[DEBUG] Positioned bee #${bee} in V formation at (${formationX}, ${formationY})`);
            }
            
            // Force immediate render update
            if (this.renderSystem) {
                this.renderSystem.update();
            }
            
            return `Formed ${bees.length} bees into V formation flying upward!`;
        } catch (error) {
            console.error('[DEBUG] Error forming bee V formation:', error);
            return `Error: ${error.message}`;
        }
    }

    // Diagnostic function to fix bee system ticking issues
    diagnoseBeeMovement() {
        try {
            console.log('[DIAGNOSTIC] Checking bee movement system...');
            
            // 1. Check if beeSystem is registered in systems map
            if (!this.systems.has('bee')) {
                console.error('[DIAGNOSTIC] BEE SYSTEM NOT REGISTERED!');
                console.log('[DIAGNOSTIC] Re-registering bee system...');
                this.systems.set('bee', beeSystem);
            }
            
            // 2. Reset the app reference in bee system (don't check directly)
            console.log('[DIAGNOSTIC] Re-setting app reference to be safe...');
            setBeeSystemApp(this.app);
            
            // 3. Manually call the bee system once with a large delta
            const forceDelta = 1000; // 1 second worth of movement in one go
            console.log('[DIAGNOSTIC] Forcing a bee system update with large delta:', forceDelta);
            beeSystem(this.world, forceDelta);
            
            // 4. Check if bees exist
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.error('[DIAGNOSTIC] No bees found!');
                console.log('[DIAGNOSTIC] Spawning test bees...');
                this.debugBeeSpawning();
                return "No bees found - spawned test bees";
            }
            
            console.log(`[DIAGNOSTIC] Found ${bees.length} bees`);
            
            // 5. Check if game loop is running
            if (!this.isRunning) {
                console.error('[DIAGNOSTIC] Game loop is not running!');
                console.log('[DIAGNOSTIC] Restarting game loop...');
                this.startGameLoop();
            }
            
            // 6. Force update all bee targets to ensure movement
            for (const bee of bees) {
                // Set targets far away from current position
                const beeX = Position.x[bee];
                const beeY = Position.y[bee];
                
                // Force bees to move in a very visible way - 20 tiles in random direction
                const angle = Math.random() * Math.PI * 2;
                const distance = TILE_SIZE * 20;
                
                AI.targetX[bee] = beeX + Math.cos(angle) * distance;
                AI.targetY[bee] = beeY + Math.sin(angle) * distance;
                
                // Force state to MOVING
                AI.state[bee] = AI_STATE.MOVING;
                Animal.isMoving[bee] = 1;
            }
            
            // 7. Force a render update
            if (this.renderSystem) {
                this.renderSystem.update();
            }
            
            // 8. Advise on next steps
            return `Diagnostics complete. ${bees.length} bees found and their targets updated. Bees should now be moving 20 tiles in random directions. If they're still not moving, try the forceFixBeePositions() function.`;
        } catch (error) {
            console.error('[DIAGNOSTIC] Error diagnosing bee movement:', error);
            return `Error: ${error.message}`;
        }
    }

    // Debug bee rendering issues
    debugBeeRendering() {
        try {
            console.log('[RENDER-DEBUG] Checking bee sprites and rendering issues...');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.error('[RENDER-DEBUG] No bees found!');
                return "No bees found";
            }
            
            console.log(`[RENDER-DEBUG] Found ${bees.length} bees to check`);
            
            // Check sprite properties and fix visibility issues
            let visibleBees = 0;
            let repairedBees = 0;
            
            for (const bee of bees) {
                const sprite = this.world.sprites[bee];
                if (!sprite) {
                    console.error(`[RENDER-DEBUG] Bee #${bee} has no sprite!`);
                    continue;
                }
                
                // Get entity position
                const entityX = Position.x[bee];
                const entityY = Position.y[bee];
                
                // Check sprite properties
                console.log(`[RENDER-DEBUG] Bee #${bee}:
                  Entity position: (${entityX.toFixed(1)}, ${entityY.toFixed(1)})
                  Sprite position: (${sprite.position.x.toFixed(1)}, ${sprite.position.y.toFixed(1)})
                  Visible: ${sprite.visible}
                  Alpha: ${sprite.alpha}
                  Z-Index: ${sprite.zIndex}
                  Has parent: ${!!sprite.parent}`);
                
                // Check if sprite is in camera view
                const cameraX = this.worldContainer.x - window.innerWidth / 2;
                const cameraY = this.worldContainer.y - window.innerHeight / 2;
                const cameraWidth = window.innerWidth;
                const cameraHeight = window.innerHeight;
                
                const inView = 
                    entityX >= cameraX && 
                    entityX <= cameraX + cameraWidth &&
                    entityY >= cameraY && 
                    entityY <= cameraY + cameraHeight;
                
                console.log(`[RENDER-DEBUG] Bee is in camera view: ${inView}`);
                
                if (inView) {
                    visibleBees++;
                }
                
                // Fix common rendering issues
                if (!sprite.visible) {
                    console.log(`[RENDER-DEBUG] Fixing visibility for bee #${bee}`);
                    sprite.visible = true;
                    repairedBees++;
                }
                
                if (sprite.alpha < 1) {
                    console.log(`[RENDER-DEBUG] Fixing alpha for bee #${bee}`);
                    sprite.alpha = 1;
                    repairedBees++;
                }
                
                // Ensure sprite is at entity position
                if (Math.abs(sprite.position.x - entityX) > 1 || Math.abs(sprite.position.y - entityY) > 1) {
                    console.log(`[RENDER-DEBUG] Fixing position mismatch for bee #${bee}`);
                    sprite.position.set(entityX, entityY);
                    repairedBees++;
                }
                
                // Ensure sprite has correct parent
                if (!sprite.parent) {
                    console.log(`[RENDER-DEBUG] Bee #${bee} sprite has no parent - adding to world container`);
                    this.worldContainer.addChild(sprite);
                    repairedBees++;
                }
                
                // Create visual indicator to make bees more visible
                const indicator = new Graphics();
                indicator.beginFill(0xFF0000, 0.5); // Bright red, semi-transparent
                indicator.drawCircle(0, 0, TILE_SIZE/2); // Circle around the bee
                indicator.endFill();
                
                // Add the indicator briefly
                indicator.position.set(entityX, entityY);
                this.worldContainer.addChild(indicator);
                
                // Remove indicator after 3 seconds
                setTimeout(() => {
                    if (indicator.parent) {
                        indicator.parent.removeChild(indicator);
                        indicator.destroy();
                    }
                }, 3000);
            }
            
            // Force renderer to update
            if (this.renderSystem) {
                console.log('[RENDER-DEBUG] Forcing renderer update');
                this.renderSystem.update();
                this.app.renderer.render(this.app.stage);
            }
            
            // Check if world container is visible
            console.log(`[RENDER-DEBUG] World container visible: ${this.worldContainer.visible}`);
            console.log(`[RENDER-DEBUG] World container alpha: ${this.worldContainer.alpha}`);
            console.log(`[RENDER-DEBUG] World container position: (${this.worldContainer.x}, ${this.worldContainer.y})`);
            console.log(`[RENDER-DEBUG] World container scale: (${this.worldContainer.scale.x}, ${this.worldContainer.scale.y})`);
            
            return `Debug complete. Found ${bees.length} bees, ${visibleBees} in view, repaired ${repairedBees} issues. Added red circles around bees for 3 seconds to make them more visible.`;
        } catch (error) {
            console.error('[RENDER-DEBUG] Error debugging bee rendering:', error);
            return `Error: ${error.message}`;
        }
    }

    // Teleport bees with visible trails
    teleportBeesWithTrails() {
        try {
            console.log('[TRAIL] Teleporting bees with visible trails...');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.error('[TRAIL] No bees found!');
                return "No bees found";
            }
            
            console.log(`[TRAIL] Found ${bees.length} bees to teleport with trails`);
            
            // For each bee, create a trail and teleport
            for (const bee of bees) {
                // Get current position
                const startX = Position.x[bee];
                const startY = Position.y[bee];
                
                // Generate random destination 10-20 tiles away
                const angle = Math.random() * Math.PI * 2;
                const distance = TILE_SIZE * (10 + Math.random() * 10);
                const endX = startX + Math.cos(angle) * distance;
                const endY = startY + Math.sin(angle) * distance;
                
                // Create trail graphics
                const trail = new Graphics();
                trail.lineStyle(3, 0xFFFF00, 0.7);  // Yellow trail
                trail.moveTo(startX, startY);
                trail.lineTo(endX, endY);
                
                // Add trail to world container
                this.worldContainer.addChild(trail);
                
                // Teleport bee to new position
                Position.x[bee] = endX;
                Position.y[bee] = endY;
                
                // Update sprite position directly
                const sprite = this.world.sprites[bee];
                if (sprite) {
                    sprite.position.set(endX, endY);
                    
                    // Make sure sprite is visible
                    sprite.visible = true;
                    sprite.alpha = 1;
                }
                
                // Remove trail after 3 seconds
                setTimeout(() => {
                    if (trail.parent) {
                        trail.parent.removeChild(trail);
                        trail.destroy();
                    }
                }, 3000);
                
                console.log(`[TRAIL] Teleported bee #${bee} from (${startX.toFixed(1)}, ${startY.toFixed(1)}) to (${endX.toFixed(1)}, ${endY.toFixed(1)})`);
            }
            
            // Force renderer to update
            if (this.renderSystem) {
                this.renderSystem.update();
                this.app.renderer.render(this.app.stage);
            }
            
            return `Teleported ${bees.length} bees with visible trails. Yellow lines show the teleport paths. Trails will disappear after 3 seconds.`;
        } catch (error) {
            console.error('[TRAIL] Error teleporting bees with trails:', error);
            return `Error: ${error.message}`;
        }
    }

    // Make bees VERY visible
    makeBeesVeryVisible() {
        try {
            console.log('[VISIBILITY] Making bees super visible...');
            
            // Get all bee entities
            const bees = this.world.queries.animals(this.world).filter(bee => 
                Animal.type && Animal.type[bee] === 4 // Type 4 = Bee
            );
            
            if (bees.length === 0) {
                console.error('[VISIBILITY] No bees found!');
                return "No bees found";
            }
            
            console.log(`[VISIBILITY] Found ${bees.length} bees to make visible`);
            
            // For each bee, replace or modify sprite to be VERY visible
            for (const bee of bees) {
                // Get current position
                const entityX = Position.x[bee];
                const entityY = Position.y[bee];
                
                // Remove existing sprite animation to prevent issues
                const existingSprite = this.world.sprites[bee];
                if (existingSprite && existingSprite.animate) {
                    if (this.app && this.app.ticker) {
                        this.app.ticker.remove(existingSprite.animate);
                    }
                    existingSprite.animate = null;
                }
                
                // Create a HUGE indicator for the bee
                const bigBeeIndicator = new Graphics();
                
                // Outer circle (yellow glow)
                bigBeeIndicator.beginFill(0xFFFF00, 0.3); // Yellow with transparency
                bigBeeIndicator.drawCircle(0, 0, TILE_SIZE * 1.5); // Very large
                bigBeeIndicator.endFill();
                
                // Middle circle (red)
                bigBeeIndicator.beginFill(0xFF0000, 0.7); // Red
                bigBeeIndicator.drawCircle(0, 0, TILE_SIZE); // Large
                bigBeeIndicator.endFill();
                
                // Inner circle (white)
                bigBeeIndicator.beginFill(0xFFFFFF, 1); // Solid white
                bigBeeIndicator.drawCircle(0, 0, TILE_SIZE / 2); // Medium
                bigBeeIndicator.endFill();
                
                // Set position
                bigBeeIndicator.position.set(entityX, entityY);
                
                // Set very high z-index to be on top
                bigBeeIndicator.zIndex = 9999;
                
                // Add to world container
                this.worldContainer.addChild(bigBeeIndicator);
                
                // Keep reference to replace default sprite
                this.world.beeIndicators = this.world.beeIndicators || {};
                this.world.beeIndicators[bee] = bigBeeIndicator;
                
                // Add text label
                const beeText = new Text(`BEE #${bee}`, {
                    fontFamily: 'Arial',
                    fontSize: 20,
                    fill: 0x000000,
                    align: 'center'
                });
                beeText.anchor.set(0.5);
                beeText.position.set(entityX, entityY - TILE_SIZE * 2);
                beeText.zIndex = 10000;
                this.worldContainer.addChild(beeText);
                
                console.log(`[VISIBILITY] Enhanced visibility for bee #${bee} at (${entityX.toFixed(1)}, ${entityY.toFixed(1)})`);
                
                // Setup continuous movement to extremely visible locations
                let moveInterval = setInterval(() => {
                    // Check if bee still exists
                    if (!this.world.entities.includes(bee) || !this.world || !this.worldContainer) {
                        clearInterval(moveInterval);
                        return;
                    }
                    
                    // Generate new position every 2 seconds
                    const currentX = Position.x[bee];
                    const currentY = Position.y[bee];
                    
                    // Move upward in a highly visible pattern - diagonally across screen
                    Position.x[bee] = currentX + TILE_SIZE * 3;
                    Position.y[bee] = currentY - TILE_SIZE * 3;
                    
                    // Update the indicator and text position
                    if (bigBeeIndicator.parent) {
                        bigBeeIndicator.position.set(Position.x[bee], Position.y[bee]);
                    }
                    
                    if (beeText.parent) {
                        beeText.position.set(Position.x[bee], Position.y[bee] - TILE_SIZE * 2);
                    }
                    
                    console.log(`[VISIBILITY] Moving bee #${bee} to (${Position.x[bee].toFixed(1)}, ${Position.y[bee].toFixed(1)})`);
                    
                    // Force update
                    if (this.renderSystem) {
                        this.renderSystem.update();
                    }
                }, 2000); // Every 2 seconds
                
                // Store interval for cleanup
                this.world.beeIntervals = this.world.beeIntervals || {};
                this.world.beeIntervals[bee] = moveInterval;
            }
            
            // Force renderer to update
            if (this.renderSystem) {
                this.renderSystem.update();
                this.app.renderer.render(this.app.stage);
            }
            
            return `Made ${bees.length} bees SUPER visible with huge colorful indicators and text labels. The bees will move in a diagonal pattern every 2 seconds.`;
        } catch (error) {
            console.error('[VISIBILITY] Error making bees visible:', error);
            return `Error: ${error.message}`;
        }
    }
} 